/**
 * Timeline - Figma `6377:88174`.
 *
 * A black band, inside the content grid (it ran to the viewport edges until 2026-10-30; what
 * runs past the grid's edge is clipped and scrolled to): previous/next buttons top left, a row of years top right, and
 * below them columns that scroll sideways. Each column shows its year set on its side
 * against a rule, with that year's entries stacked at the foot of the column.
 *
 * **The content model is flat and the design is grouped.** `shared.timeline-item` is one
 * year plus one month, so several items share a year; the renderer groups them. That is
 * the right way round - an editor adds one event at a time and does not have to think
 * about columns.
 *
 * The year row is navigation, not decoration: each year scrolls its column into view.
 * The columns are a real scroll container, so a trackpad, a touch screen and a keyboard
 * all work without the buttons.
 *
 * The band paints its own black rather than relying on the section's `background`
 * setting - it is intrinsic to this design, not an editor's choice. The file's band is
 * `#000000` (sampled from 6377:88174); it was `ink-strong`, a dark grey, until 2026-10-30.
 * `fullBleed` is ignored for the same reason as in `pillars`: pages saved with it ticked
 * would otherwise drop out of the grid.
 */
import { SectionShell } from '../primitives';
import { ScrollerButtons, TRACK, useScroller } from '../shared/Scroller';
import type { TimelineItem, TimelineSection } from '../../types/sections';

/** Items grouped by year, in the order the years first appear. */
function groupByYear(items: TimelineItem[]): { year: string; entries: TimelineItem[] }[] {
  const order: string[] = [];
  const byYear = new Map<string, TimelineItem[]>();

  for (const item of items) {
    const year = item.year ?? '';
    if (!byYear.has(year)) {
      byYear.set(year, []);
      order.push(year);
    }
    byYear.get(year)!.push(item);
  }

  return order.map((year) => ({ year, entries: byYear.get(year)! }));
}

export default function Timeline({ data }: { data: TimelineSection }) {
  const { track, scrollBy, scrollTo, range } = useScroller();
  const years = groupByYear(data.items ?? []);
  const label = data.heading ?? 'Historia';

  if (!years.length) return null;

  return (
    <SectionShell settings={{ ...data.settings, fullBleed: false }} name="timeline">
      {data.heading ? <h2 className="text-h4 text-ink">{data.heading}</h2> : null}

      <div className="overflow-hidden rounded-l bg-black px-5 py-10 text-white sm:px-8 lg:px-10">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <ScrollerButtons label={label} scrollBy={scrollBy} range={range} tone="dark" />

          <nav aria-label={`${label}: lata`} className="flex flex-wrap gap-4">
            {years.map((group, i) => (
              <button
                key={group.year}
                type="button"
                onClick={() => scrollTo(i)}
                className="text-body-sm text-white/60 underline-offset-8 transition hover:text-white hover:underline"
              >
                {group.year}
              </button>
            ))}
          </nav>
        </div>

        <div
          ref={track}
          role="group"
          aria-label={label}
          tabIndex={0}
          className={`${TRACK} mt-10`}
        >
          {years.map((group) => (
            <section
              key={group.year}
              className="flex min-h-[320px] w-[300px] shrink-0 snap-start flex-col justify-between gap-10 border-l border-white/30 pl-6"
            >
              <p aria-hidden className="text-h1 leading-none [writing-mode:vertical-rl] rotate-180">
                {group.year}
              </p>
              <h3 className="sr-only">{group.year}</h3>

              <div className="flex flex-col gap-6">
                {group.entries.map((entry, i) => (
                  <div key={i} className="flex flex-col gap-1">
                    {entry.month ? <p className="text-body-sm font-semibold">{entry.month}</p> : null}
                    {entry.title ? <p className="text-body-sm">{entry.title}</p> : null}
                    {entry.body ? <p className="text-body-sm text-white/70">{entry.body}</p> : null}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </SectionShell>
  );
}
